import { useUpdateOrganisationNameMutation } from '@/app/redux/endpoints/organisation';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { downloadDocxFile, generateLetterDocx } from '@/common/utils/documents';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import { Label } from '@shadcn/components/label';
import { Separator } from '@shadcn/components/separator';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  SettingsContentContainer,
  SettingsHeaderContainer,
  SettingsHeaderText,
  SettingsPageContainer,
  SettingsSectionHeader,
} from './SettingsPageStyles';

export default function OrganisationDetails() {
  const { organisation } = useOrganisation();
  const [updateOrganisationName] = useUpdateOrganisationNameMutation();

  const [newOrganisationName, setNewOrganisationName] = useState(
    organisation?.name ?? '',
  );

  async function handleUpdateOrganisationName() {
    if (!organisation) {
      console.error('Missing org');
      toast.error('Failed to update organisation name', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    try {
      const oldName = organisation?.name;
      await updateOrganisationName({
        orgId: organisation.id,
        name: newOrganisationName,
      }).unwrap();

      trackMixpanelEvent('Organisation Name Changed', {
        oldName,
        newName: newOrganisationName,
      });

      toast.success('Organisation name updated');
    } catch (error) {
      toast.error('Failed to update organisation name', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  const handleDownloadLetterTemplate = async () => {
    try {
      const doc = generateLetterDocx(
        organisation?.name ?? 'Unnamed Organisation',
      );
      await downloadDocxFile(doc, 'letter-home-template.docx');
      toast.success('Template downloaded');
      trackMixpanelEvent('Letter Template Downloaded');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  };

  const handleDownloadFlyer = () => {
    try {
      const flyerUrl = '/organisation_resources/spellcaster_flyer.pdf';

      const a = document.createElement('a');
      a.href = flyerUrl;
      a.download = 'spellcaster_flyer.pdf';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast.success('Flyer downloaded');
      trackMixpanelEvent('Flyer Downloaded');
    } catch (error) {
      console.error('Error downloading flyer:', error);
      toast.error('Failed to download flyer', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  };

  return (
    <SettingsPageContainer>
      <SettingsHeaderContainer>
        <SettingsHeaderText>
          <h2>Organisation Details</h2>
          <div>Manage your organisation details here</div>
        </SettingsHeaderText>
      </SettingsHeaderContainer>

      <SettingsContentContainer>
        <div className="flex flex-col gap-2">
          <Label>Organisation Name</Label>
          <div className="flex items-center gap-4">
            <Input
              className="w-[300px]"
              value={newOrganisationName}
              onChange={(e) => setNewOrganisationName(e.target.value)}
            />
            <Button
              onClick={handleUpdateOrganisationName}
              disabled={
                !newOrganisationName ||
                organisation?.name === newOrganisationName
              }
            >
              Update
            </Button>
          </div>
        </div>

        <Separator className="my-4" />

        <div className="flex flex-col gap-4">
          <SettingsSectionHeader>Helpful Documents</SettingsSectionHeader>

          <div className="flex flex-col gap-2">
            <Label>Letter Home Template</Label>
            <div className="flex items-center gap-4">
              <p className="text-sm text-muted-foreground">
                Download a template letter that can be customized and sent to
                parents/guardians.
              </p>
            </div>
            <Button
              onClick={handleDownloadLetterTemplate}
              className="w-[200px] mt-2"
            >
              Download Template
            </Button>
          </div>

          <Separator className="my-2" />

          <div className="flex flex-col gap-2">
            <Label>Spellcaster Flyer</Label>
            <div className="flex items-center gap-4">
              <p className="text-sm text-muted-foreground">
                Download a printable flyer to share information about
                Spellcaster with parents and the school community.
              </p>
            </div>
            <Button onClick={handleDownloadFlyer} className="w-[200px] mt-2">
              Download Flyer
            </Button>
          </div>
        </div>
      </SettingsContentContainer>
    </SettingsPageContainer>
  );
}
